/**
 * Notification handling - audio, push notifications, visual effects
 */

// Audio context for notification sounds
let audioContext = null;


/**
 * Play notification sound
 */
export function playNotificationSound() {
    try {
        if (!audioContext) {
            audioContext = new (window.AudioContext || window.webkitAudioContext)();
        }

        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Quick rising tone
        oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(880, audioContext.currentTime + 0.1);
        oscillator.frequency.exponentialRampToValueAtTime(1320, audioContext.currentTime + 0.2);

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
        console.log('Audio notification not available');
    }
}


/**
 * Show visual flash effect for new closest alert.
 * Reuses a single persistent DOM element instead of creating/removing on each call.
 * @param {string} service - Service type for color
 */
let _flashEl = null;
let _flashTimer = null;

export function showAlertFlash(service) {
    if (!_flashEl) {
        _flashEl = document.createElement('div');
        document.body.appendChild(_flashEl);
    }
    // Reset animation by removing and re-adding
    clearTimeout(_flashTimer);
    _flashEl.className = `new-alert-flash ${service}`;
    // Force reflow to restart CSS animation
    void _flashEl.offsetWidth;
    _flashTimer = setTimeout(() => { _flashEl.className = ''; }, 600);
}


// =============================================================================
// Toast Notifications
// =============================================================================

let toastContainer = null;

/**
 * Get or create toast container
 */
function getToastContainer() {
    if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.className = 'toast-container';
        toastContainer.setAttribute('role', 'alert');
        toastContainer.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastContainer);
    }
    return toastContainer;
}

/**
 * Show a toast notification
 * @param {string} message - The message to display
 * @param {string} type - Toast type: 'info', 'error', 'success', 'warning'
 * @param {number} duration - Duration in ms (default 4000)
 */
export function showToast(message, type = 'info', duration = 4000) {
    const container = getToastContainer();

    const toast = document.createElement('div');
    toast.className = `toast toast-${type}`;

    // Add icon based on type
    const icons = {
        error: '⚠️',
        success: '✓',
        warning: '⚡',
        info: 'ℹ️'
    };
    toast.textContent = `${icons[type] || ''} ${message}`;

    container.appendChild(toast);

    // Remove after duration
    setTimeout(() => {
        toast.classList.add('toast-out');
        setTimeout(() => toast.remove(), 300);
    }, duration);

    return toast;
}

/**
 * Show error toast
 */
export function showError(message) {
    return showToast(message, 'error', 5000);
}

/**
 * Show warning toast
 */
export function showWarning(message) {
    return showToast(message, 'warning', 4000);
}
